import {
  createPathElement,
  flattenPage,
  insertElements,
  normalizeStroke,
  type PathBrush,
  pathHit,
  removeElements,
} from "@cva/shared";
import { Eraser, Highlighter, PenLine, PenTool } from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";
import { create } from "zustand";
import { cn } from "../lib/utils";
import { ColorButton } from "./ColorPicker";
import { addLinePreset, LINE_PRESETS, linePresetPreview } from "./LineProperties";
import type { EditorStore } from "./store";
import { activePageDoc, pagePan, useEditor, useEditorApi, usePagePan } from "./store";
import { Slider, Tip } from "./ui";

/**
 * Drawing tool ("Zeichnen"): while active, drags on the canvas draw freehand
 * strokes that become `path` elements (one undo step each, synced like any
 * other change). The eraser removes whole strokes it touches.
 */

export type DrawBrush = PathBrush | "eraser";

type BrushSettings = { width: number; opacity: number };

const BRUSHES: { id: DrawBrush; label: string; icon: typeof PenLine }[] = [
  { id: "pen", label: "Stift", icon: PenLine },
  { id: "marker", label: "Marker", icon: PenTool },
  { id: "highlighter", label: "Textmarker", icon: Highlighter },
  { id: "eraser", label: "Radierer", icon: Eraser },
];

/** Default widths relative to the shorter page side, and opacities. */
const DEFAULTS: Record<DrawBrush, { width: number; opacity: number }> = {
  pen: { width: 0.005, opacity: 1 },
  marker: { width: 0.015, opacity: 0.85 },
  highlighter: { width: 0.03, opacity: 0.5 },
  eraser: { width: 0.02, opacity: 1 },
};

type DrawToolState = {
  active: boolean;
  brush: DrawBrush;
  color: string;
  highlighterColor: string;
  /** Settings per brush in document pixels; set when the tool is first opened in a document. */
  settings: Record<DrawBrush, BrushSettings> | null;
  scaleFor: number | null;
  setActive: (active: boolean, store?: EditorStore) => void;
  setBrush: (brush: DrawBrush) => void;
  setColor: (color: string) => void;
  setSetting: (patch: Partial<BrushSettings>) => void;
};

function defaultSettings(shortSide: number): Record<DrawBrush, BrushSettings> {
  const out = {} as Record<DrawBrush, BrushSettings>;
  for (const [brush, value] of Object.entries(DEFAULTS) as [DrawBrush, BrushSettings][]) {
    out[brush] = {
      width: Math.max(1, Math.round(value.width * shortSide)),
      opacity: value.opacity,
    };
  }
  return out;
}

export const useDrawTool = create<DrawToolState>((set, get) => ({
  active: false,
  brush: "pen",
  color: "#1a1a1a",
  highlighterColor: "#ffd60a",
  settings: null,
  scaleFor: null,
  setActive(active, store) {
    if (active && store) {
      const { readOnly } = store.getState();
      const doc = activePageDoc(store);
      if (readOnly) return;
      const shortSide = Math.min(doc.format.width, doc.format.height);
      if (get().scaleFor !== shortSide) {
        set({ settings: defaultSettings(shortSide), scaleFor: shortSide });
      }
      store.getState().select([]);
      store.getState().setEditingText(null);
    }
    set({ active });
  },
  setBrush: (brush) => set({ brush }),
  setColor: (color) => set(get().brush === "highlighter" ? { highlighterColor: color } : { color }),
  setSetting(patch) {
    const { settings, brush } = get();
    if (!settings) return;
    set({ settings: { ...settings, [brush]: { ...settings[brush], ...patch } } });
  },
}));

export function toggleDrawMode(store: EditorStore) {
  const tool = useDrawTool.getState();
  tool.setActive(!tool.active, store);
}

type Point = { x: number; y: number };

/** Transparent layer over the canvas that captures pointer drags while drawing. */
export function DrawLayer({
  viewportRef,
}: {
  viewportRef: React.RefObject<HTMLDivElement | null>;
}) {
  const store = useEditorApi();
  const active = useDrawTool((s) => s.active);
  const brush = useDrawTool((s) => s.brush);
  const color = useDrawTool((s) => (s.brush === "highlighter" ? s.highlighterColor : s.color));
  const settings = useDrawTool((s) => s.settings?.[s.brush]);
  const zoom = useEditor((s) => s.zoom);
  const pan = usePagePan();
  const readOnly = useEditor((s) => s.readOnly);
  const [points, setPoints] = useState<Point[] | null>(null);
  const [pointer, setPointer] = useState<Point | null>(null);
  const drawing = useRef<{ points: Point[]; erase: string | null } | null>(null);

  useEffect(() => {
    if (readOnly && active) useDrawTool.getState().setActive(false);
  }, [readOnly, active]);
  // Leaving the editor ends draw mode (the next document opens with the selection tool).
  useEffect(() => () => useDrawTool.getState().setActive(false), []);

  if (!active || !settings) return null;

  const toWorld = (event: ReactPointerEvent): Point => {
    const rect = viewportRef.current!.getBoundingClientRect();
    const { zoom: z } = store.getState();
    const p = pagePan(store.getState());
    return { x: (event.clientX - rect.left - p.x) / z, y: (event.clientY - rect.top - p.y) / z };
  };

  const erase = (point: Point, merge: string) => {
    const state = store.getState();
    const radius = settings.width / 2;
    const hits = flattenPage(state.doc, state.pageIndex)
      .filter(
        ({ element, box, hidden }) =>
          element.type === "path" &&
          !hidden &&
          !element.locked &&
          pathHit(element, box, point, radius),
      )
      .map(({ element }) => element.id);
    if (hits.length > 0) state.update((draft) => removeElements(draft, hits), { merge });
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = toWorld(event);
    if (brush === "eraser") {
      const merge = `draw-erase-${Date.now()}`;
      drawing.current = { points: [point], erase: merge };
      erase(point, merge);
      return;
    }
    drawing.current = { points: [point], erase: null };
    setPoints([point]);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const point = toWorld(event);
    setPointer(point);
    const current = drawing.current;
    if (!current) return;
    // Coalesced events give smoother strokes on fast drags.
    const native = event.nativeEvent;
    const samples =
      typeof native.getCoalescedEvents === "function" ? native.getCoalescedEvents() : [];
    const rect = viewportRef.current!.getBoundingClientRect();
    const { zoom: z } = store.getState();
    const p = pagePan(store.getState());
    const added =
      samples.length > 0
        ? samples.map((sample) => ({
            x: (sample.clientX - rect.left - p.x) / z,
            y: (sample.clientY - rect.top - p.y) / z,
          }))
        : [point];
    if (current.erase) {
      for (const sample of added) erase(sample, current.erase);
      return;
    }
    current.points.push(...added);
    setPoints([...current.points]);
  };

  const finish = () => {
    const current = drawing.current;
    drawing.current = null;
    setPoints(null);
    if (!current || current.erase || brush === "eraser") return;
    const stroke = normalizeStroke(current.points, settings.width);
    if (!stroke) return;
    const element = createPathElement({
      name: BRUSHES.find((entry) => entry.id === brush)?.label ?? "Zeichnung",
      x: stroke.x,
      y: stroke.y,
      width: stroke.width,
      height: stroke.height,
      d: stroke.d,
      viewBox: stroke.viewBox,
      color,
      strokeWidth: settings.width,
      brush,
      opacity: settings.opacity,
    });
    const { pageIndex } = store.getState();
    store.getState().update((draft) => insertElements(draft, pageIndex, [element]));
  };

  const screen = (point: Point) => `${point.x * zoom + pan.x},${point.y * zoom + pan.y}`;
  return (
    <>
      <div
        className="absolute inset-0 z-10"
        style={{ cursor: brush === "eraser" ? "none" : "crosshair" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finish}
        onPointerCancel={finish}
        onPointerLeave={() => setPointer(null)}
        data-draw-layer=""
      >
        <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
          {points && points.length > 0 ? (
            <polyline
              points={points.map(screen).join(" ")}
              fill="none"
              stroke={color.slice(0, 7)}
              strokeOpacity={settings.opacity}
              strokeWidth={settings.width * zoom}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ mixBlendMode: brush === "highlighter" ? "multiply" : undefined }}
            />
          ) : null}
          {pointer && brush === "eraser" ? (
            <circle
              cx={pointer.x * zoom + pan.x}
              cy={pointer.y * zoom + pan.y}
              r={Math.max(3, (settings.width / 2) * zoom)}
              fill="rgb(255 255 255 / 0.4)"
              stroke="#52525b"
              strokeWidth={1}
            />
          ) : null}
        </svg>
      </div>
      <DrawToolbar />
    </>
  );
}

/** Floating bar over the canvas: brush, colour, width, opacity, done. */
function DrawToolbar() {
  const tool = useDrawTool();
  const settings = tool.settings?.[tool.brush];
  if (!settings) return null;
  const color = tool.brush === "highlighter" ? tool.highlighterColor : tool.color;
  return (
    <div
      className="absolute top-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2 shadow-lg"
      onPointerDown={(event) => event.stopPropagation()}
      role="toolbar"
      aria-label="Zeichnen"
    >
      <div className="flex gap-1">
        {BRUSHES.map((entry) => (
          <Tip key={entry.id} label={entry.label}>
            <button
              type="button"
              aria-label={entry.label}
              aria-pressed={tool.brush === entry.id}
              onClick={() => tool.setBrush(entry.id)}
              className={cn(
                "inline-flex size-8 items-center justify-center rounded-md text-zinc-600 [&_svg]:size-4",
                tool.brush === entry.id ? "bg-brand-50 text-brand-700" : "hover:bg-zinc-100",
              )}
            >
              <entry.icon />
            </button>
          </Tip>
        ))}
      </div>
      {tool.brush !== "eraser" ? (
        <ColorButton value={color} onChange={(next) => tool.setColor(next)} label="Farbe" />
      ) : null}
      <label className="flex items-center gap-2 text-xs text-zinc-500">
        {tool.brush === "eraser" ? "Größe" : "Stärke"}
        <Slider
          value={settings.width}
          min={1}
          max={Math.max(40, (tool.scaleFor ?? 1000) * 0.1)}
          onChange={(width) => tool.setSetting({ width: Math.round(width) })}
          className="w-24"
        />
        <span className="w-6 tabular-nums">{settings.width}</span>
      </label>
      {tool.brush !== "eraser" ? (
        <label className="flex items-center gap-2 text-xs text-zinc-500">
          Deckkraft
          <Slider
            value={Math.round(settings.opacity * 100)}
            min={10}
            max={100}
            onChange={(value) => tool.setSetting({ opacity: value / 100 })}
            className="w-20"
          />
        </label>
      ) : null}
      <button
        type="button"
        onClick={() => tool.setActive(false)}
        className="inline-flex h-8 items-center rounded-md bg-brand-600 px-3 text-xs font-medium text-white hover:bg-brand-700"
      >
        Fertig
      </button>
    </div>
  );
}

/** Elements panel: line & arrow presets and the drawing tool. */
export function LinesAndDrawing() {
  const store = useEditorApi();
  const active = useDrawTool((s) => s.active);
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-2">
        {LINE_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            title={preset.label}
            aria-label={preset.label}
            onClick={() => addLinePreset(store, preset.id)}
            className="flex aspect-square items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 text-zinc-600 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
          >
            {linePresetPreview(preset.id)}
          </button>
        ))}
        <button
          type="button"
          title="Zeichnen (P)"
          aria-pressed={active}
          onClick={() => toggleDrawMode(store)}
          className={cn(
            "flex aspect-square flex-col items-center justify-center gap-0.5 rounded-lg border text-[10px] font-medium transition [&_svg]:size-5",
            active
              ? "border-brand-500 bg-brand-50 text-brand-700"
              : "border-zinc-200 bg-zinc-50 text-zinc-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700",
          )}
        >
          <PenLine />
          Zeichnen
        </button>
      </div>
    </div>
  );
}
